import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { glassPanel } from "../../components/layout/UserLayout/designTokens";
import API from "../../config/api";
import {
  HiOutlineUser,
  HiOutlineArrowRightOnRectangle,
  HiOutlineClipboardDocumentList,
  HiOutlineChartBar,
  HiOutlineBolt,
  HiOutlineHome,
  HiOutlinePlus,
  HiOutlineHeart,
  HiOutlineBookOpen,
  HiOutlineLockClosed,
  HiOutlineKey,
} from "react-icons/hi2";

/**
 * Page de profil utilisateur
 * Affiche les informations de l'utilisateur et ses statistiques
 */
const ProfilePage = ({ user, token, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    totalRecipes: 0,
    totalFavorites: 0,
    recentActivity: 0,
  });
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState({
    type: null,
    text: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [usernameMessage, setUsernameMessage] = useState({
    type: null,
    text: "",
  });
  const [usernameLoading, setUsernameLoading] = useState(false);

  /**
   * Charger les statistiques
   * Cette fonction est appelée automatiquement au montage du composant
   * pour récupérer les statistiques de l'utilisateur (nombre de recettes, favoris)
   */
  useEffect(() => {
    if (user && token) {
      loadStats();
    }
  }, [user, token]);

  /**
   * Fonction asynchrone pour charger les statistiques de l'utilisateur.
   * Elle fait deux requêtes API en parallèle :
   * 1. Récupère toutes les recettes de l'utilisateur connecté
   * 2. Récupère tous les favoris de l'utilisateur connecté
   * Puis calcule et met à jour les statistiques affichées.
   */
  const loadStats = async () => {
    try {
      setLoading(true); // Active l'indicateur de chargement

      // 1. Charger les recettes de l'utilisateur connecté.
      // Le backend filtre automatiquement pour retourner uniquement les recettes de l'utilisateur
      // identifié par le token JWT dans les headers.
      const recipesResponse = await fetch("http://localhost:3000/api/recipes", {
        headers: {
          Authorization: `Bearer ${token}`, // Token JWT pour authentifier la requête
        },
      });

      let totalRecipes = 0;
      if (recipesResponse.ok) {
        const recipesData = await recipesResponse.json();
        // Compte le nombre de recettes retournées par le backend
        totalRecipes = recipesData.data?.length || 0;
      }

      // 2. Charger les favoris de l'utilisateur connecté.
      // Le backend retourne tous les favoris de l'utilisateur identifié par le token JWT.
      const favoritesResponse = await fetch(
        "http://localhost:3000/api/favorites",
        {
          headers: {
            Authorization: `Bearer ${token}`, // Token JWT pour authentifier la requête
          },
        },
      );

      let totalFavorites = 0;
      if (favoritesResponse.ok) {
        const favoritesData = await favoritesResponse.json();
        // Compte le nombre de favoris retournés par le backend
        totalFavorites = favoritesData.data?.length || 0;
      }

      // 3. Mettre à jour l'état avec les statistiques calculées.
      // Ces valeurs seront affichées dans les cartes de statistiques.
      setStats({
        totalRecipes,
        totalFavorites,
        recentActivity: 0, // Pour l'instant, cette valeur est fixée à 0
      });
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
    } finally {
      setLoading(false); // Désactive l'indicateur de chargement, même en cas d'erreur
    }
  };

  /**
   * Gérer la déconnexion de l'utilisateur.
   * Cette fonction supprime toutes les données d'authentification stockées
   * et redirige l'utilisateur vers la page d'accueil.
   */
  const handleLogout = () => {
    // 1. Supprimer le token JWT du localStorage pour invalider la session
    localStorage.removeItem("token");
    // 2. Supprimer les données utilisateur du localStorage
    localStorage.removeItem("user");
    // 3. Appeler la fonction onLogout passée en props pour mettre à jour l'état global de l'application
    onLogout(null, null);
    // 4. Rediriger l'utilisateur vers la page d'accueil
    navigate("/");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: null, text: "" });
    if (newPassword.length < 6) {
      setPasswordMessage({
        type: "error",
        text: t("newPassword") + " (min. 6 caractères)",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({
        type: "error",
        text: t("confirmPassword") + " ne correspond pas.",
      });
      return;
    }
    setPasswordLoading(true);
    try {
      const url = `${API.baseURL}/users/profile/password`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token || localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setPasswordMessage({
          type: "success",
          text: t("passwordChangedSuccess"),
        });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordMessage({
          type: "error",
          text: data.message || t("passwordCurrentWrong"),
        });
      }
    } catch (err) {
      setPasswordMessage({
        type: "error",
        text: err.message || t("passwordCurrentWrong"),
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveUsername = async (e) => {
    e.preventDefault();
    const username = editUsername.trim();
    setUsernameMessage({ type: null, text: "" });
    if (!username || username.length < 3) {
      setUsernameMessage({
        type: "error",
        text: t("username") + " (min. 3 caractères)",
      });
      return;
    }
    const current = currentUser?.username ?? "";
    if (username === current) {
      return;
    }
    setUsernameLoading(true);
    try {
      const url = `${API.baseURL}/users/profile`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token || localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ username }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data) {
        setUsernameMessage({
          type: "success",
          text: t("usernameUpdatedSuccess"),
        });
        localStorage.setItem("user", JSON.stringify(data.data));
        if (typeof onLogout === "function") {
          onLogout(data.data, token || localStorage.getItem("token"));
        }
      } else {
        setUsernameMessage({
          type: "error",
          text: data.message || t("usernameTaken"),
        });
      }
    } catch (err) {
      setUsernameMessage({
        type: "error",
        text: err.message || t("usernameTaken"),
      });
    } finally {
      setUsernameLoading(false);
    }
  };

  // Vérifier l'authentification (props ou localStorage)
  const currentUser =
    user ||
    (() => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (error) {
          console.error("❌ Erreur parsing localStorage:", error);
          return null;
        }
      }
      return null;
    })();

  // Initialiser le champ nom d'utilisateur avec la valeur courante
  useEffect(() => {
    if (currentUser?.username && editUsername === "") {
      setEditUsername(currentUser.username);
    }
  }, [currentUser?.username]);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ember-50 via-ink-50 to-pine-50 flex items-center justify-center">
        <div className="text-center">
          <HiOutlineLockClosed
            className="w-16 h-16 mx-auto mb-4 text-ink-400"
            aria-hidden
          />
          <h2 className="text-2xl font-bold text-ink-800 mb-2">
            {t("unauthorizedAccess")}
          </h2>
          <p className="text-ink-600 mb-6">{t("mustBeLoggedIn")}</p>
          <button
            onClick={() => navigate("/auth")}
            className="px-6 py-3 bg-ember-600 text-white rounded-xl font-semibold hover:bg-ember-500 transition-colors"
          >
            {t("login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full py-4 sm:py-6 w-full min-w-0">
      <div className="container mx-auto px-4 sm:px-6 max-w-[1800px]">
        {/* En-tête */}
        <div className={`${glassPanel} p-4 sm:p-6 mb-4 sm:mb-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-ink-900 dark:text-white mb-1 sm:mb-2 flex items-center gap-2">
                <HiOutlineUser
                  className="w-8 h-8 sm:w-9 sm:h-9 text-amber-400 shrink-0"
                  aria-hidden
                />
                {t("myProfile")}
              </h1>
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">
                {t("manageAccount")}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto px-4 py-2 bg-ink-100 hover:bg-ink-200 text-ink-700 rounded-lg border border-ink-900/20 transition-colors shrink-0 flex items-center justify-center gap-2 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/40"
            >
              <HiOutlineArrowRightOnRectangle className="w-5 h-5 shrink-0" />
              {t("logout")}
            </button>
          </div>
        </div>

        {/* Informations personnelles : nom d'utilisateur, email, prénom, nom + changement mot de passe */}
        <div className={`${glassPanel} p-4 sm:p-6 mb-4 sm:mb-6`}>
          <h2 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
            <HiOutlineClipboardDocumentList className="w-6 h-6 text-amber-400/90 shrink-0" />
            {t("personalInformation")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Nom d'utilisateur (modifiable) */}
            <div className="md:col-span-2">
              <form onSubmit={handleSaveUsername} className="space-y-2">
                <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                  {t("username")}
                </label>
                <div className="flex flex-wrap items-end gap-2">
                  <input
                    type="text"
                    value={editUsername}
                    onChange={(e) => setEditUsername(e.target.value)}
                    className="flex-1 min-w-[200px] px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20 placeholder-ink-400 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                    placeholder={t("newUsernamePlaceholder")}
                    minLength={3}
                    maxLength={50}
                    autoComplete="username"
                  />
                  <button
                    type="submit"
                    disabled={
                      usernameLoading ||
                      editUsername.trim() === (currentUser?.username ?? "")
                    }
                    className="px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    {usernameLoading ? "..." : t("saveUsername")}
                  </button>
                </div>
                {usernameMessage.text && (
                  <p
                    className={`text-sm ${
                      usernameMessage.type === "success"
                        ? "text-green-700 dark:text-green-300"
                        : "text-red-700 dark:text-red-300"
                    }`}
                  >
                    {usernameMessage.text}
                  </p>
                )}
              </form>
            </div>

            <div>
              <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                {t("email")}
              </label>
              <div className="px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20">
                {currentUser.email}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                {t("firstName")}
              </label>
              <div className="px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20">
                {currentUser.first_name || t("notSpecified")}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                {t("lastName")}
              </label>
              <div className="px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20">
                {currentUser.last_name || t("notSpecified")}
              </div>
            </div>
          </div>

          {/* Réinitialiser le mot de passe (dans la même section) */}
          <div className="mt-6 pt-6 border-t border-ink-900/10 dark:border-white/20">
            <h3 className="text-base font-bold text-ink-900 dark:text-white mb-3 flex items-center gap-2">
              <HiOutlineKey className="w-5 h-5 text-amber-400/90 shrink-0" />
              {t("changePassword")}
            </h3>
            <form
              onSubmit={handleChangePassword}
              className="space-y-4 max-w-md"
            >
              <div>
                <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                  {t("currentPassword")}
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20 placeholder-ink-400 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                  {t("newPassword")}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20 placeholder-ink-400 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink-700 dark:text-white/90 mb-2">
                  {t("confirmPassword")}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-ink-50 dark:bg-white/20 rounded-lg text-ink-900 dark:text-white border border-ink-900/15 dark:border-white/20 placeholder-ink-400 dark:placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              {passwordMessage.text && (
                <p
                  className={`text-sm ${
                    passwordMessage.type === "success"
                      ? "text-green-700 dark:text-green-300"
                      : "text-red-700 dark:text-red-300"
                  }`}
                >
                  {passwordMessage.text}
                </p>
              )}
              <button
                type="submit"
                disabled={passwordLoading}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {passwordLoading ? "..." : t("changePasswordButton")}
              </button>
            </form>
          </div>
        </div>

        {/* Statistiques */}
        <div className={`${glassPanel} p-4 sm:p-6 mb-4 sm:mb-6`}>
          <h2 className="text-lg sm:text-xl font-bold text-ink-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
            <HiOutlineChartBar className="w-6 h-6 text-amber-400/90 shrink-0" />
            {t("myStatistics")}
          </h2>
          {loading ? (
            <div className="text-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-ink-200 border-t-ink-600 dark:border-white/30 dark:border-t-white mx-auto mb-4"></div>
              <p className="text-ink-700 dark:text-white text-sm sm:text-base">
                {t("loadingStats")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center p-6 bg-ink-50 dark:bg-white/20 rounded-xl border border-ink-900/10 dark:border-white/20">
                <div className="text-3xl font-bold text-ink-900 dark:text-white mb-2">
                  {stats.totalRecipes}
                </div>
                <div className="text-ink-600 dark:text-white/90">{t("recipesCreated")}</div>
              </div>
              <div className="text-center p-6 bg-ink-50 dark:bg-white/20 rounded-xl border border-ink-900/10 dark:border-white/20">
                <div className="text-3xl font-bold text-ink-900 dark:text-white mb-2">
                  {stats.totalFavorites}
                </div>
                <div className="text-ink-600 dark:text-white/90">{t("favorites")}</div>
              </div>
              <div className="text-center p-6 bg-ink-50 dark:bg-white/20 rounded-xl border border-ink-900/10 dark:border-white/20">
                <div className="text-3xl font-bold text-ink-900 dark:text-white mb-2">
                  {stats.recentActivity}
                </div>
                <div className="text-ink-600 dark:text-white/90">{t("recentActivity")}</div>
              </div>
            </div>
          )}
        </div>

        {/* Actions rapides */}
        <div className={`${glassPanel} p-6`}>
          <h2 className="text-xl font-bold text-ink-900 dark:text-white mb-4 flex items-center gap-2">
            <HiOutlineBolt className="w-6 h-6 text-amber-400 shrink-0" />
            {t("quickActions")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-4 bg-ink-50 hover:bg-ink-100 rounded-xl text-ink-900 font-semibold transition-all duration-300 transform hover:scale-105 text-left border border-ink-900/10 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/20"
            >
              <HiOutlineHome
                className="w-8 h-8 mb-2 text-amber-400/90"
                aria-hidden
              />
              <div className="font-bold text-ink-900 dark:text-white">{t("myDashboard")}</div>
              <div className="text-sm text-ink-600 dark:text-white/90">{t("viewMyRecipes")}</div>
            </button>

            <button
              onClick={() => navigate("/add-recipe")}
              className="p-4 bg-ink-50 hover:bg-ink-100 rounded-xl text-ink-900 font-semibold transition-all duration-300 transform hover:scale-105 text-left border border-ink-900/10 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/20"
            >
              <HiOutlinePlus
                className="w-8 h-8 mb-2 text-amber-400/90"
                aria-hidden
              />
              <div className="font-bold text-ink-900 dark:text-white">{t("addRecipe")}</div>
              <div className="text-sm text-ink-600 dark:text-white/90">{t("shareCreation")}</div>
            </button>

            <button
              onClick={() => navigate("/favorites")}
              className="p-4 bg-ink-50 hover:bg-ink-100 rounded-xl text-ink-900 font-semibold transition-all duration-300 transform hover:scale-105 text-left border border-ink-900/10 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/20"
            >
              <HiOutlineHeart
                className="w-8 h-8 mb-2 text-amber-400/90"
                aria-hidden
              />
              <div className="font-bold text-ink-900 dark:text-white">{t("myFavorites")}</div>
              <div className="text-sm text-ink-600 dark:text-white/90">{t("savedRecipes")}</div>
            </button>

            <button
              onClick={() => navigate("/recipes")}
              className="p-4 bg-ink-50 hover:bg-ink-100 rounded-xl text-ink-900 font-semibold transition-all duration-300 transform hover:scale-105 text-left border border-ink-900/10 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/20"
            >
              <HiOutlineBookOpen
                className="w-8 h-8 mb-2 text-amber-400/90"
                aria-hidden
              />
              <div className="font-bold text-ink-900 dark:text-white">{t("allRecipes")}</div>
              <div className="text-sm text-ink-600 dark:text-white/90">
                {t("discoverRecipes")}
              </div>
            </button>

            <button
              onClick={handleLogout}
              className="p-4 bg-ink-50 hover:bg-ink-100 rounded-xl text-ink-900 font-semibold transition-all duration-300 transform hover:scale-105 text-left border border-ink-900/10 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white dark:border-white/20"
            >
              <HiOutlineArrowRightOnRectangle
                className="w-8 h-8 mb-2 text-amber-400/90"
                aria-hidden
              />
              <div className="font-bold text-ink-900 dark:text-white">{t("logout")}</div>
              <div className="text-sm text-ink-600 dark:text-white/90">{t("disconnect")}</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
